import OnboardingSurveyModal from '@components/OnboardingSurveyModal'
import { TestIds } from '@/testIds'
import { useAuth } from '@context/AuthContext'
import { useMe } from '@hooks/useMe'
import { isSurveyPending } from '@lib/onboardingSurvey'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Header from '../Header'

const LayoutContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	overflow: hidden;
`

const ContentWrapper = styled.div`
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
`

interface LayoutProps {
	children: React.ReactNode
	showBackButton?: boolean
	backButtonText?: string
	onBackClick?: () => void
}

const Layout: React.FC<LayoutProps> = ({
	children,
	showBackButton = false,
	backButtonText = 'Back',
	onBackClick,
}) => {
	const navigate = useNavigate()
	const { logout, user: authUser } = useAuth()
	const { data: user } = useMe()
	const isAdmin = user?.role === 'ADMIN'
	// Pending is keyed to the User created at signup and survives refreshes; the modal
	// clears it on answer or skip, so it shows at most once overall. Derived at render
	// time because the auth session hydrates in an effect after first mount.
	const [surveyDismissed, setSurveyDismissed] = React.useState(false)
	const showSurvey = !surveyDismissed && isSurveyPending(authUser?.id)

	const handleLogout = () => {
		logout()
		navigate('/')
	}

	const handleBack = () => {
		if (onBackClick) {
			onBackClick()
		} else {
			navigate(-1) // Default: go back in browser history
		}
	}

	const handleMyTripsClick = () => {
		navigate('/trips')
	}

	const handleMyProfileClick = () => {
		navigate('/profile')
	}

	const handleAdminClick = () => {
		navigate('/admin')
	}

	return (
		<LayoutContainer data-testid={TestIds.mainTabBar.screen}>
			<Header
				onLogout={handleLogout}
				showBackButton={showBackButton}
				onBackClick={handleBack}
				backButtonText={backButtonText}
				onMyTripsClick={handleMyTripsClick}
				onMyProfileClick={handleMyProfileClick}
				onAdminClick={isAdmin ? handleAdminClick : undefined}
			/>
			<ContentWrapper>
				{children}
			</ContentWrapper>
			<OnboardingSurveyModal isOpen={showSurvey} onClose={() => setSurveyDismissed(true)} />
		</LayoutContainer>
	)
}

export default Layout
